import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form", "submitButton", "characterContainer", "characterTemplate", "character",
    "composition", "primaryProfession", "secondaryProfession", "attributeRow",
    "attributeSelect", "attributeRank", "attributeRankValue", "increaseRankButton",
    "decreaseRankButton", "attributeError", "attributeTemplate", "budgetError",
    "attributesContainer", "spent", "remaining", "skill", "skillError",
    "formError", "addCharacterButton"
  ]

  static values = {
    attributeOptions: Object,
    attributeNames: Object,
    pointCosts: Array,
    maxCharacters: Number,
    maxAdditions: Number
  }

  connect() {
    this.updateAll()
    this.updateComposition()
  }

  compositionChanged() {
    this.updateComposition()
    this.updateCharacterLimit()
    this.updateAll()
    this.updateComposition()
  }

  professionChanged(event) {
    const character = event.currentTarget.closest("[data-build-editor-character]")
    this.refreshAttributeOptions(character)
    this.updateCharacter(character)
  }

  attributeChanged(event) {
    const row = event.currentTarget.closest("[data-build-editor-attribute]")
    const character = event.currentTarget.closest("[data-build-editor-character]")
    row.dataset.attributeId = event.currentTarget.value
    this.refreshAttributeOptions(character)
    this.updateCharacter(character)
  }

  rankChanged(event) {
    this.updateCharacter(event.currentTarget.closest("[data-build-editor-character]"))
  }

  increaseRank(event) {
    this.adjustAttributeRank(event.currentTarget, 1)
  }

  decreaseRank(event) {
    this.adjustAttributeRank(event.currentTarget, -1)
  }

  skillChanged(event) {
    this.updateCharacter(event.currentTarget.closest("[data-build-editor-character]"))
  }

  addAttribute(event) {
    const character = event.currentTarget.closest("[data-build-editor-character]")
    const template = character.querySelector("[data-build-editor-target='attributeTemplate']")
    const currentIndices = Array.from(character.querySelectorAll("[data-build-editor-target='attributeSelect']"))
      .map((select) => select.id.match(/-(?:new-)?(\d+)$/)?.[1])
      .filter((index) => index !== undefined)
      .map(Number)
    const nextIndex = Math.max(-1, ...currentIndices) + 1
    const rowTemplate = document.createElement("template")
    rowTemplate.innerHTML = template.innerHTML.replaceAll("__ATTRIBUTE_INDEX__", String(nextIndex))
    const row = rowTemplate.content.firstElementChild
    event.currentTarget.before(row)
    this.refreshAttributeOptions(character)
    this.updateCharacter(character)
    row.querySelector("select")?.focus()
  }

  removeAttribute(event) {
    const character = event.currentTarget.closest("[data-build-editor-character]")
    event.currentTarget.closest("[data-build-editor-attribute]").remove()
    this.updateCharacter(character)
  }

  adjustAttributeRank(button, change) {
    const row = button.closest("[data-build-editor-attribute]")
    const rankInput = row.querySelector("[data-build-editor-target='attributeRank']")
    const rank = Number(rankInput.value)
    if (!Number.isInteger(rank) || rank < 0 || rank > 12) return

    const nextRank = Math.max(0, Math.min(12, rank + change))
    if (nextRank === rank) return

    rankInput.value = String(nextRank)
    this.updateCharacter(row.closest("[data-build-editor-character]"))
  }

  addCharacter(event) {
    const createMode = this.hasCompositionTarget
    const currentCount = this.characterTargets.length
    const mode = createMode ? this.compositionTargets.find((input) => input.checked)?.value : "team"
    const additions = this.characterTargets.filter((character) => character.dataset.characterAddition === "true").length
    if (createMode && (mode !== "team" || currentCount >= this.maxCharactersValue)) return
    if (!createMode && additions >= this.maxAdditionsValue) return

    const index = createMode ? currentCount : additions
    const markup = this.characterTemplateTarget.innerHTML.replaceAll("__INDEX__", String(index))
    const template = document.createElement("template")
    template.innerHTML = markup
    const character = template.content.firstElementChild
    if (!character) return

    if (!createMode) character.dataset.characterAddition = "true"
    this.characterContainerTarget.append(character)
    this.refreshAttributeOptions(character)
    this.updateAll()
    this.updateComposition()
    character.querySelector("input[name$='[name]']")?.focus()
  }

  validateSubmit(event) {
    this.updateAll()
    if (!this.hasInvalidValues) return

    event.preventDefault()
    this.formErrorTarget.textContent = "Fix invalid professions, attributes, or skills before saving."
    this.formErrorTarget.focus?.()
  }

  updateAll() {
    this.characterTargets.forEach((character) => {
      this.refreshAttributeOptions(character)
    })
    this.updateCharacter()
    this.updateCharacterLimit()
  }

  updateCharacter() {
    const singleMode = this.hasCompositionTarget && this.compositionTargets.find((input) => input.checked)?.value === "single"
    const characters = singleMode ? this.characterTargets.slice(0, 1) : this.characterTargets
    this.hasInvalidValues = characters.some((character) => this.validateCharacter(character))
    this.submitButtonTarget.disabled = this.hasInvalidValues
    this.formErrorTarget.textContent = ""
  }

  validateCharacter(character) {
    const primary = Number(character.querySelector("[data-build-editor-target='primaryProfession']")?.value)
    const secondary = Number(character.querySelector("[data-build-editor-target='secondaryProfession']")?.value)
    const professionsValid = Number.isInteger(primary) && primary >= 1 && primary <= 10 &&
      Number.isInteger(secondary) && secondary >= 0 && secondary <= 10 && primary !== secondary
    let spent = 0
    let invalid = !professionsValid
    const attributeSelects = Array.from(character.querySelectorAll("[data-build-editor-target='attributeSelect']"))
    const selectedAttributeCounts = attributeSelects.reduce((counts, select) => {
      if (select.value !== "") counts[select.value] = (counts[select.value] || 0) + 1
      return counts
    }, {})

    character.querySelectorAll("[data-build-editor-attribute]").forEach((row) => {
      const attribute = row.querySelector("[data-build-editor-target='attributeSelect']")
      const rankInput = row.querySelector("[data-build-editor-target='attributeRank']")
      const error = row.querySelector("[data-build-editor-target='attributeError']")
      const attributeId = attribute.value
      const rankText = rankInput.value
      const rank = Number(rankText)
      const rankValid = /^\d+$/.test(rankText) && Number.isInteger(rank) && rank >= 0 && rank <= 12
      const available = attributeId === "" || this.availableAttributeIds(character).includes(Number(attributeId))
      const clearedUnavailable = !available && rankValid && rank === 0
      const duplicate = attributeId !== "" && selectedAttributeCounts[attributeId] > 1
      row.querySelector("[data-build-editor-target='attributeRankValue']").textContent = rankText
      row.querySelector("[data-build-editor-target='decreaseRankButton']").disabled = clearedUnavailable || !rankValid || rank <= 0
      row.querySelector("[data-build-editor-target='increaseRankButton']").disabled = clearedUnavailable || !rankValid || rank >= 12

      row.dataset.available = String(available)
      row.dataset.duplicate = String(duplicate)
      row.classList.toggle("border", duplicate || (!available && !clearedUnavailable))
      attribute.setAttribute("aria-invalid", String(duplicate || (!available && !clearedUnavailable)))
      rankInput.setAttribute("aria-invalid", String(!rankValid || (!available && !clearedUnavailable)))
      attribute.disabled = clearedUnavailable
      rankInput.disabled = clearedUnavailable
      if (duplicate) {
        error.textContent = "This attribute is already selected in another row."
      } else if (!available) {
        error.textContent = clearedUnavailable
          ? "This unavailable allocation will be omitted when saved."
          : "This attribute is unavailable. Set its rank to zero before saving."
      } else {
        error.textContent = rankValid ? "" : "Rank must be an integer from 0 through 12."
      }
      error.classList.toggle("hidden", !duplicate && available && rankValid)
      if (!rankValid || duplicate) invalid = true

      if (attributeId !== "") {
        if (!rankValid || (!available && !clearedUnavailable)) invalid = true
        if (rankValid) spent += this.pointCostsValue[rank]
      }
    })

    character.querySelectorAll("[data-build-editor-target='skill']").forEach((input) => {
      const slot = input.closest("[data-build-skill-order-target='slot']")
      const skillId = Number(input.value)
      const professionId = Number(input.dataset.skillProfessionId)
      const isPve = input.dataset.skillPve === "true"
      const eligible = skillId === 0 || (Number.isInteger(skillId) && Number.isInteger(professionId) &&
        (isPve || professionId === 0 || professionId === primary || professionId === secondary))
      const error = slot.querySelector("[data-build-editor-target='skillError']")
      slot.classList.toggle("border-red-500", !eligible)
      error.classList.toggle("hidden", eligible)
      if (!eligible) invalid = true
    })

    const spentTarget = character.querySelector("[data-build-editor-target='spent']")
    const remainingTarget = character.querySelector("[data-build-editor-target='remaining']")
    const budgetError = character.querySelector("[data-build-editor-target='budgetError']")
    spentTarget.textContent = String(spent)
    remainingTarget.textContent = String(200 - spent)
    const overBudget = spent > 200
    budgetError.classList.toggle("hidden", !overBudget)
    if (overBudget) invalid = true
    return invalid
  }

  refreshAttributeOptions(character) {
    if (!character) return

    const availableIds = this.availableAttributeIds(character)
    const selects = Array.from(character.querySelectorAll("[data-build-editor-target='attributeSelect']"))
    const selectedIds = selects.map((select) => select.value).filter((id) => id !== "")
    selects.forEach((select) => {
      const selected = select.value
      const currentName = select.selectedOptions[0]?.textContent
      const options = [{ id: "", name: "Choose attribute" }, ...availableIds.map((id) => ({ id: String(id), name: this.attributeNamesValue[String(id)] }))]
      if (selected && !availableIds.includes(Number(selected))) options.push({ id: selected, name: currentName })

      select.replaceChildren(...options.map(({ id, name }) => {
        const option = document.createElement("option")
        option.value = id
        option.textContent = name
        option.disabled = id !== "" && id !== selected &&
          (!availableIds.includes(Number(id)) || selectedIds.includes(id))
        return option
      }))
      select.value = selected
    })
  }

  updateComposition() {
    if (!this.hasCompositionTarget) return

    const singleMode = this.compositionTargets.find((input) => input.checked)?.value === "single"
    this.characterTargets.slice(1).forEach((character) => {
      character.hidden = singleMode
      character.querySelectorAll("input, select, textarea, button").forEach((control) => {
        if (singleMode) {
          if (!control.hasAttribute("data-composition-was-disabled")) {
            control.dataset.compositionWasDisabled = String(control.disabled)
          }
          control.disabled = true
        } else if (control.hasAttribute("data-composition-was-disabled")) {
          control.disabled = control.dataset.compositionWasDisabled === "true"
          delete control.dataset.compositionWasDisabled
        }
      })
    })
  }

  availableAttributeIds(character) {
    const primary = character.querySelector("[data-build-editor-target='primaryProfession']")?.value
    const secondary = character.querySelector("[data-build-editor-target='secondaryProfession']")?.value
    return [...new Set([
      ...(this.attributeOptionsValue[primary] || []),
      ...(this.attributeOptionsValue[secondary] || [])
    ])]
  }

  updateCharacterLimit() {
    if (!this.hasAddCharacterButtonTarget) return

    const createMode = this.hasCompositionTarget
    const currentCount = this.characterTargets.length
    const mode = createMode ? this.compositionTargets.find((input) => input.checked)?.value : "team"
    const additions = this.characterTargets.filter((character) => character.dataset.characterAddition === "true").length
    const atLimit = createMode ? currentCount >= this.maxCharactersValue : additions >= this.maxAdditionsValue
    this.addCharacterButtonTarget.hidden = (createMode && mode !== "team") || atLimit
  }
}
